import React, { useEffect, useMemo, useState } from "react";

const OVERLAY_Z_INDEX = 2000;
const CARD_WIDTH = 320;
const CARD_GAP = 14;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const StudioOnboardingTour = ({
  isOpen,
  steps,
  activeStep,
  onClose,
  onNext,
  onPrev,
  onFinish,
  targetRects,
}) => {
  const [cardPosition, setCardPosition] = useState({
    top: 120,
    left: 120,
  });

  const currentStep = useMemo(() => steps?.[activeStep] || null, [steps, activeStep]);

  useEffect(() => {
    if (!isOpen || !currentStep) return;

    const targetRect = targetRects?.[currentStep.targetKey];

    if (!targetRect) {
      setCardPosition({
        top: 120,
        left: 120,
      });
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = targetRect.right + CARD_GAP;
    let top = targetRect.top;

    if (left + CARD_WIDTH > viewportWidth - 20) {
      left = targetRect.left - CARD_WIDTH - CARD_GAP;
    }

    if (left < 20) {
      left = clamp(targetRect.left, 20, viewportWidth - CARD_WIDTH - 20);
      top = targetRect.bottom + CARD_GAP;
    }

    if (top + 220 > viewportHeight - 20) {
      top = clamp(viewportHeight - 240, 20, viewportHeight - 240);
    }

    setCardPosition({
      top: clamp(top, 20, Math.max(20, viewportHeight - 240)),
      left: clamp(left, 20, Math.max(20, viewportWidth - CARD_WIDTH - 20)),
    });
  }, [isOpen, currentStep, targetRects]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleResize = () => {
      const event = new Event("resize");
      window.dispatchEvent(event);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [isOpen]);

  if (!isOpen || !currentStep) return null;

  const targetRect = targetRects?.[currentStep.targetKey];
  const isLastStep = activeStep === steps.length - 1;

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.45)",
          zIndex: OVERLAY_Z_INDEX,
        }}
      />

      {targetRect && (
        <div
          style={{
            position: "fixed",
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
            border: "2px solid #6366f1",
            borderRadius: "16px",
            boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.35)",
            zIndex: OVERLAY_Z_INDEX + 1,
            pointerEvents: "none",
          }}
        />
      )}

      <div
        style={{
          position: "fixed",
          top: cardPosition.top,
          left: cardPosition.left,
          width: `${CARD_WIDTH}px`,
          background: "#ffffff",
          borderRadius: "18px",
          boxShadow: "0 18px 40px rgba(15, 23, 42, 0.25)",
          padding: "18px",
          zIndex: OVERLAY_Z_INDEX + 2,
        }}
      >
        <div
          style={{
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: "#6366f1",
            textTransform: "uppercase",
            marginBottom: "8px",
          }}
        >
          Studio Tour
        </div>

        <h3
          style={{
            margin: "0 0 8px",
            fontSize: "18px",
            color: "#111827",
          }}
        >
          {currentStep.title}
        </h3>

        <p
          style={{
            margin: 0,
            color: "#4b5563",
            lineHeight: "1.6",
            fontSize: "14px",
          }}
        >
          {currentStep.description}
        </p>

        <div
          style={{
            marginTop: "14px",
            display: "flex",
            gap: "6px",
            flexWrap: "wrap",
          }}
        >
          {steps.map((step, index) => (
            <span
              key={step.id}
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "999px",
                background: index === activeStep ? "#6366f1" : "#d1d5db",
                display: "inline-block",
              }}
            />
          ))}
        </div>

        <div
          style={{
            marginTop: "18px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              color: "#6b7280",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Skip
          </button>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              onClick={onPrev}
              disabled={activeStep === 0}
              style={{
                padding: "10px 14px",
                borderRadius: "10px",
                border: "1px solid #d1d5db",
                background: "#ffffff",
                color: activeStep === 0 ? "#9ca3af" : "#111827",
                cursor: activeStep === 0 ? "not-allowed" : "pointer",
                fontWeight: 600,
              }}
            >
              Back
            </button>

            {isLastStep ? (
              <button
                type="button"
                onClick={onFinish}
                style={{
                  padding: "10px 14px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#111827",
                  color: "#ffffff",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Finish
              </button>
            ) : (
              <button
                type="button"
                onClick={onNext}
                style={{
                  padding: "10px 14px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#111827",
                  color: "#ffffff",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StudioOnboardingTour;